import React, { useMemo, useState } from 'react';
import { X, Twitter, Facebook, Linkedin, MessageCircle, Link, Copy, Check } from 'lucide-react';
import { Post } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface ShareModalProps {
  post: Post;
  isOpen: boolean;
  onClose: () => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ post, isOpen, onClose }) => {
  const { select } = useLanguage();
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/post/${post.id}`;
  const shareText = select({
    es: `Mira esta increíble obra: "${post.title}" por @${post.author.username} en SoArtistic`,
    en: `Check out this amazing piece: "${post.title}" by @${post.author.username} on SoArtistic`
  });
  
  const shareOptions = useMemo(
    () => [
      {
        name: 'Twitter',
        icon: Twitter,
        color: 'bg-blue-500 hover:bg-blue-600',
        url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`
      },
      {
        name: 'Facebook',
        icon: Facebook,
        color: 'bg-blue-600 hover:bg-blue-700',
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
      },
      {
        name: 'Instagram',
        icon: () => (
          <div className="w-5 h-5 bg-gradient-to-tr from-purple-600 via-pink-600 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xs">
            IG
          </div>
        ),
        color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
        url: `https://www.instagram.com/`
      },
      {
        name: 'LinkedIn',
        icon: Linkedin,
        color: 'bg-blue-700 hover:bg-blue-800',
        url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
      },
      {
        name: 'WhatsApp',
        icon: MessageCircle,
        color: 'bg-green-500 hover:bg-green-600',
        url: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`
      }
    ],
    [shareText, shareUrl]
  );

  if (!isOpen) return null;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = (url: string) => {
    window.open(url, '_blank', 'width=550,height=420');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-0 md:p-4 z-[9999]">
      <div className="bg-white w-full h-full md:rounded-2xl md:max-w-md md:w-full md:h-auto overflow-y-auto modal-detail">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">{select({ es: 'Compartir obra', en: 'Share artwork' })}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-target"
              aria-label={select({ es: 'Cerrar', en: 'Close' })}
            >
              <X size={20} />
            </button>
          </div>

          {/* Post Preview */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              {post.media && post.media[0] && (
                <img
                  src={post.media[0].url}
                  alt={post.title}
                  className="w-12 h-12 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 line-clamp-1">{post.title}</h3>
                <p className="text-sm text-gray-500">{select({ es: 'Por', en: 'By' })} @{post.author.username}</p>
              </div>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-3 mb-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-medium text-gray-700 mb-3">{select({ es: 'Compartir en redes sociales', en: 'Share on social media' })}</h3>
            <div className="grid grid-cols-2 gap-3">
              {shareOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.name}
                    onClick={() => {
                      if (option.name === 'Instagram') {
                        // For Instagram, copy text and open app
                        navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                        alert(select({ es: 'Texto copiado. Abriendo Instagram...', en: 'Text copied. Opening Instagram...' }));
                        window.open('https://www.instagram.com/', '_blank');
                      } else {
                        handleShare(option.url);
                      }
                    }}
                    className={`flex items-center gap-3 p-3 rounded-lg text-white transition-colors ${
                      option.name === 'Instagram' ? option.color : option.color
                    }`}
                  >
                    {typeof Icon === 'function' ? <Icon /> : <Icon size={20} />}
                    <span className="font-medium">{option.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Copy Link */}
          <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-medium text-gray-700">{select({ es: 'O copia el enlace', en: 'Or copy the link' })}</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
              />
              <button
                onClick={copyToClipboard}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  copied
                    ? 'bg-green-600 text-white'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {copied ? (
                  <div className="flex items-center gap-2">
                    <Check size={16} />
                    <span>{select({ es: 'Copiado', en: 'Copied' })}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Copy size={16} />
                    <span>{select({ es: 'Copiar', en: 'Copy' })}</span>
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Native Share (if available) */}
          {navigator.share && (
            <div className="mt-4 pt-4 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => {
                  navigator.share({
                    title: post.title,
                    text: shareText,
                    url: shareUrl,
                  }).catch(err => console.log('Error sharing:', err));
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors touch-target"
              >
                <Link size={16} />
                <span>{select({ es: 'Compartir usando el sistema', en: 'Share using system share' })}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
